import { useEffect, useState } from 'react';
import { getEc2Health } from '../lib/callables.js';

type HealthState = 'loading' | 'online' | 'starting' | 'stopped' | 'offline' | 'error';

export function Ec2HealthBadge() {
  const [state, setState] = useState<HealthState>('loading');
  const [lastPingMs, setLastPingMs] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const result = await getEc2Health();
        if (cancelled) return;
        const { online, lastPingMs: ping, instanceState } = result.data;
        setLastPingMs(ping);
        if (online) {
          setState('online');
        } else if (instanceState === 'pending' || instanceState === 'running') {
          setState('starting');
        } else if (
          instanceState === 'stopped' ||
          instanceState === 'stopping'
        ) {
          setState('stopped');
        } else {
          setState('offline');
        }
      } catch {
        if (!cancelled) setState('error');
      }
    }

    check();
    const interval = setInterval(check, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const config: Record<HealthState, { dot: string; label: string }> = {
    loading: { dot: 'bg-[#aeaaa4]', label: 'Checking…' },
    online: {
      dot: 'bg-[#0f7b6c]',
      label:
        lastPingMs != null
          ? `EC2 online · ${Math.round(lastPingMs / 1000)}s ago`
          : 'EC2 online',
    },
    starting: { dot: 'bg-[#d9730d]', label: 'EC2 starting…' },
    stopped: { dot: 'bg-[#aeaaa4]', label: 'EC2 stopped' },
    offline: { dot: 'bg-[#e03e3e]', label: 'EC2 unreachable' },
    error: { dot: 'bg-[#d9730d]', label: 'Health check failed' },
  };

  const { dot, label } = config[state];

  return (
    <div className="flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot} ${state === 'online' ? 'animate-pulse' : ''}`} />
      <span className="text-xs text-[#73726e]">{label}</span>
    </div>
  );
}
